import React from 'react';
import { colors } from '../../styles/tokens';
import { useEsMovil } from '../../hooks/useEsMovil';
import { fmtInt, fmtMoney } from '../../utils/format';
import { metaColor } from '../../utils/meta';

export interface AvanceMetaProps {
  metaMonto: number;
  ventaMonto: number;
  faltanteMonto: number;
  cumplimientoMonto: number | null;
  metaPares: number;
  ventaPares: number;
  faltantePares: number;
  cumplimientoPares: number | null;
  /** Fracción del periodo ya corrida. `null` = no aplica (rango inválido). */
  ritmoEsperado: number | null;
  diasTranscurridos: number;
  diasDelPeriodo: number;
}

interface BarraProps {
  titulo: string;
  meta: number;
  venta: number;
  falta: number;
  cumplimiento: number | null;
  ritmo: number | null;
  formato: (n: number) => string;
  unidad: string;
  /** Disposición de teléfono: la barra pasa a ancho completo. */
  esMovil: boolean;
}

/**
 * Una barra donde la meta es la pista, la venta el relleno y lo que falta el
 * resto ETIQUETADO.
 *
 * El marcador vertical de ritmo es lo que convierte la barra en una decisión:
 * «llevo 60 %» no dice nada sin «y debería llevar 71 %». Sin él, cada tienda
 * parece ir mal el día 3 del mes y bien el día 28.
 */
const Barra: React.FC<BarraProps> = ({
  titulo,
  meta,
  venta,
  falta,
  cumplimiento,
  ritmo,
  formato,
  unidad,
  esMovil,
}) => {
  const color = metaColor(cumplimiento);
  // La barra se dibuja sobre la meta. Si se superó, el relleno llega al 100 %
  // y el exceso se dice con el número, no estirando la barra fuera de su caja.
  const anchoRelleno = meta > 0 ? Math.min(100, (venta / meta) * 100) : 0;
  const superada = cumplimiento !== null && cumplimiento >= 1;
  const ritmoPct = ritmo === null ? null : Math.min(100, Math.max(0, ritmo * 100));

  return (
    <div
      style={{
        flex: 1,
        // En escritorio las dos barras comparten fila y `minWidth: 260` es lo
        // que las obliga a envolverse antes de quedar ilegibles. En teléfono ya
        // van apiladas, y ese mismo mínimo haría que la barra se negara a
        // encogerse por debajo de 260 px: con el acolchado de la tarjeta eso
        // desborda los 390 px de pantalla. `minWidth: 0` es lo que le permite
        // a un hijo de flex encogerse de verdad.
        minWidth: esMovil ? 0 : 260,
        width: esMovil ? '100%' : undefined,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: esMovil ? 8 : undefined,
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '.12em',
            color: colors.texto.tertiary,
          }}
        >
          {titulo}
        </span>
        <span style={{ fontSize: 13, color: colors.texto.secondary }}>
          meta {formato(meta)}
        </span>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          marginBottom: 10,
          // Red de seguridad: la cifra grande va en `nowrap` y no se encoge, así
          // que si algún día el importe creciera hasta no dejar sitio al
          // porcentaje, éste baja de línea en vez de empujar la tarjeta fuera
          // de la pantalla.
          flexWrap: esMovil ? 'wrap' : undefined,
        }}
      >
        <span
          style={{
            fontSize: 30,
            fontWeight: 800,
            color: colors.texto.primary,
            letterSpacing: '-.02em',
            // La cifra grande se queda en 30 px: medido a 390 px,
            // "$191,505,911" ocupa ~190 px y con el porcentaje al lado sobra
            // sitio. Lo que NO puede pasar es que el importe se parta por la
            // mitad en dos líneas, así que en teléfono se fija `nowrap` (en
            // escritorio nunca ha envuelto y no se toca, para no alterar el
            // render actual).
            whiteSpace: esMovil ? 'nowrap' : undefined,
          }}
        >
          {formato(venta)}
        </span>
        <span style={{ fontSize: 15, fontWeight: 800, color }}>
          {cumplimiento === null ? '—' : `${(cumplimiento * 100).toFixed(1)} %`}
        </span>
      </div>

      <div
        role="img"
        aria-label={`${titulo}: ${formato(venta)} de ${formato(meta)}${
          ritmoPct === null ? '' : `, ritmo esperado ${ritmoPct.toFixed(0)} %`
        }`}
        style={{
          position: 'relative',
          // Un par de píxeles más de alto en teléfono: la barra es el objeto
          // que se mira y, con el marcador de ritmo encima, 16 px se leen
          // justos a la distancia de un móvil.
          height: esMovil ? 18 : 16,
          borderRadius: 999,
          background: '#eef0f5',
          border: `1px solid ${colors.border}`,
          overflow: 'hidden',
        }}
      >
        <div
          data-testid="relleno"
          style={{
            width: `${anchoRelleno}%`,
            height: '100%',
            background: color,
            borderRadius: 999,
            transition: 'width .4s ease',
          }}
        />
        {ritmoPct !== null && (
          // El marcador va FUERA del relleno para que se vea aunque la venta lo
          // haya rebasado: la pregunta es si vas por delante o por detrás.
          <div
            data-testid="marcador-ritmo"
            title={`Ritmo esperado: ${ritmoPct.toFixed(0)} %`}
            style={{
              position: 'absolute',
              left: `${ritmoPct}%`,
              top: -3,
              bottom: -3,
              // 2 px se pierden en una pantalla de teléfono, y este marcador es
              // justo lo que hace útil la barra: sin él, «llevo 60 %» no dice
              // nada. 3 px lo mantienen visible sin taparla.
              width: esMovil ? 3 : 2,
              background: colors.texto.primary,
              opacity: 0.75,
            }}
          />
        )}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          // Las dos glosas ("Falta $8,494,089" y "ritmo esperado 71 %") caben en
          // una línea a 390 px, pero con importes más largos podrían no hacerlo:
          // se deja que la segunda baje, en vez de encoger la letra.
          flexWrap: esMovil ? 'wrap' : undefined,
          gap: esMovil ? 4 : undefined,
          marginTop: 7,
          fontSize: 12.5,
          color: colors.texto.secondary,
        }}
      >
        <span>
          {superada ? (
            <>
              <strong style={{ color: colors.verde }}>Meta cubierta</strong>
              {' · sobra '}
              {formato(venta - meta)}
            </>
          ) : (
            <>
              {'Falta '}
              <strong style={{ color: colors.texto.primary }}>{formato(falta)}</strong>
              {` ${unidad}`}
            </>
          )}
        </span>
        {ritmoPct !== null && <span>ritmo esperado {ritmoPct.toFixed(0)} %</span>}
      </div>
    </div>
  );
};

/**
 * Tarjeta de cabecera del tablero de Presupuesto: dinero y pares, uno al lado
 * del otro.
 *
 * ⚠️ LOS PARES NO SON DECORACIÓN. Cuando el dinero y el volumen divergen —una
 * tienda que cumple en pesos con 12 % menos pares— la explicación es el precio
 * medio, y eso sólo se ve teniendo las dos barras juntas. La semana 33 real:
 * 97,0 % en pesos contra 92,7 % en pares.
 *
 * En teléfono «juntas» pasa a ser una debajo de la otra, a ancho completo: se
 * siguen leyendo de un vistazo sin desplazarse de lado, que es lo que hace
 * falta para compararlas.
 */
const AvanceMeta: React.FC<AvanceMetaProps> = ({
  metaMonto,
  ventaMonto,
  faltanteMonto,
  cumplimientoMonto,
  metaPares,
  ventaPares,
  faltantePares,
  cumplimientoPares,
  ritmoEsperado,
  diasTranscurridos,
  diasDelPeriodo,
}) => {
  const esMovil = useEsMovil();

  return (
    <div
      style={{
        background: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: 18,
        // 24 px de acolchado lateral se comen 48 de los 390 de pantalla: en
        // teléfono baja a 14, el margen lateral del resto del módulo.
        padding: esMovil ? '16px 14px' : '20px 24px',
        boxShadow: '0 1px 2px rgba(20,28,54,.05),0 16px 38px -26px rgba(20,28,54,.35)',
      }}
    >
      <div
        style={{
          display: 'flex',
          // El título y el contador de días juntos pasan de 340 px: en una fila
          // de teléfono el contador se partiría o pisaría al título, así que se
          // apilan.
          flexDirection: esMovil ? 'column' : 'row',
          alignItems: esMovil ? 'flex-start' : 'baseline',
          justifyContent: 'space-between',
          gap: esMovil ? 3 : undefined,
          marginBottom: esMovil ? 14 : 18,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: colors.texto.primary }}>
          Avance contra el presupuesto
        </h3>
        <span style={{ fontSize: 12.5, color: colors.texto.tertiary }}>
          {diasTranscurridos} de {diasDelPeriodo} días del periodo
        </span>
      </div>

      <div
        style={{
          display: 'flex',
          // Una barra debajo de la otra. El `flexWrap` de escritorio no basta:
          // sin `column` la primera barra se queda en los 260 px de su
          // `minWidth` y no llena el ancho de la pantalla.
          flexDirection: esMovil ? 'column' : 'row',
          gap: esMovil ? 18 : 34,
          flexWrap: 'wrap',
        }}
      >
        <Barra
          titulo="PESOS"
          meta={metaMonto}
          venta={ventaMonto}
          falta={faltanteMonto}
          cumplimiento={cumplimientoMonto}
          ritmo={ritmoEsperado}
          formato={fmtMoney}
          unidad=""
          esMovil={esMovil}
        />
        <Barra
          titulo="PARES"
          meta={metaPares}
          venta={ventaPares}
          falta={faltantePares}
          cumplimiento={cumplimientoPares}
          ritmo={ritmoEsperado}
          formato={fmtInt}
          unidad="pares"
          esMovil={esMovil}
        />
      </div>
    </div>
  );
};

export default AvanceMeta;
